import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PatientService from "services/PatientService";
import { focusHospital } from "state/slices/hospitalSlice";
import Cookies from "universal-cookie";

const cookies = new Cookies();
const ALL_CENTRES_HOSPITAL_ID = 0;
const PAGE_SIZE = 25;

function GlobalPatientSearch() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [options, setOptions] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(0);
  const searchRef = useRef("");
  const requestIdRef = useRef(0);

  const fetchPatients = useCallback(async (search, page, append = false) => {
    const requestId = ++requestIdRef.current;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await PatientService.getAllPatients([
        ALL_CENTRES_HOSPITAL_ID,
        page,
        PAGE_SIZE,
        search,
      ]);

      if (requestId !== requestIdRef.current) return;

      const data = response.data || {};
      const content = data.content || [];

      setOptions((prev) => (append ? [...prev, ...content] : content));
      setHasMore(page + 1 < (data.totalPages ?? 0));
      pageRef.current = page;
      searchRef.current = search;
    } catch (error) {
      if (requestId === requestIdRef.current) {
        console.error("Failed to search patients", error);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    if (inputValue.trim().length < 2) {
      requestIdRef.current += 1;
      setOptions([]);
      setHasMore(false);
      pageRef.current = 0;
      searchRef.current = "";
      return;
    }

    const timerId = setTimeout(() => {
      fetchPatients(inputValue.trim(), 0, false);
    }, 300);

    return () => clearTimeout(timerId);
  }, [inputValue, fetchPatients]);

  const handleListboxScroll = useCallback(
    (event) => {
      const listbox = event.currentTarget;
      const nearBottom =
        listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 24;

      if (nearBottom && hasMore && !loading && !loadingMore) {
        fetchPatients(searchRef.current, pageRef.current + 1, true);
      }
    },
    [fetchPatients, hasMore, loading, loadingMore]
  );

  function handlePatientSelect(patient) {
    if (!patient?.id) return;

    const centreId = Number(
      patient.associatedHospitalId ?? patient.hospitalId
    );
    if (centreId > 0) {
      cookies.set("hospital", centreId, { path: "/" });
      dispatch(focusHospital({ id: centreId }));
    }

    // Navigate after focusing centre so patient profile APIs use the right hospital.
    navigate(`/hospital/patients/${patient.id}/profile`);
  }

  return (
    <Autocomplete
      size="small"
      sx={{ width: { xs: 220, md: 360 } }}
      options={options}
      loading={loading}
      autoComplete
      includeInputInList
      openOnFocus
      filterOptions={(x) => x}
      ListboxProps={{
        onScroll: handleListboxScroll,
        style: { maxHeight: 320 },
      }}
      getOptionLabel={(option) => {
        const name = `${option.firstName || ""} ${option.lastName || ""}`.trim();
        return name || `Patient #${option.id}`;
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box sx={{ py: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {`${option.firstName || ""} ${option.lastName || ""}`.trim() || "Unnamed Patient"}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Patient ID: {option.id}
              {option.associatedHospitalName
                ? ` · ${option.associatedHospitalName}`
                : ""}
            </Typography>
          </Box>
        </Box>
      )}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={(_, value) => handlePatientSelect(value)}
      noOptionsText={
        inputValue.trim().length < 2
          ? "Search by name, email, or patient ID"
          : "No patients found. Try full name, email, or patient ID."
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search patients"
          placeholder="Name, email, or patient ID"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || loadingMore ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default GlobalPatientSearch;
